<template>
  <section
    :id="module.id"
    class="text-section"
    :class="[isNestedModule ? '-nested' : '', gridSpanClass]"
    :style="[textSectionElStyles]"
  >
    <GlobalUIMedia
      v-if="module.config.general.showMedia"
      :media="module.config.general.media"
      :lazy="lazy"
    />
    <div class="text-wrapper" :style="[modulePadding, nestedModulePadding]">
      <h2
        v-if="showHeader(module)"
        class="header"
        :style="headerElStyles"
        v-html="module.config.header.text"
      ></h2>
      <div
        v-if="module.config.general.showText"
        class="text"
        :style="textElStyles"
        v-html="module.config.text.text"
      ></div>
      <GlobalUICtaButton
        v-if="module.config.general.showCtaButton"
        :style="ctaElStyles"
        :cta-button="module.config.ctaButton"
        @click="$emit('emittedClick')"
      />
    </div>
  </section>
</template>

<script>
import textModule from "./textDefaultModuleProp.js";

import { globalComputed, globalMethods } from "../GlobalUI/mixins/global.js";
import { galleryChildrenComputed } from "../Gallery/GalleryChildrenMethods.ts";
import GlobalUICtaButton from "../GlobalUI/GlobalUICtaButton.vue";
import GlobalUIMedia from "../GlobalUI/GlobalUIMedia.vue";

export default {
  name: "Text",
  components: { GlobalUICtaButton, GlobalUIMedia },
  mixins: [globalComputed, globalMethods, galleryChildrenComputed],
  props: {
    // when used in a gallery module, this is used for adjustments
    isNestedModule: {
      type: Boolean,
      default: false,
    },
    // should only be filled when its a nested module
    parent: {
      type: Object,
      default() {
        return {};
      },
    },
    module: {
      type: Object,
      default() {
        return textModule;
      },
    },
    lazy: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    textSectionElStyles() {
      const { backgroundColor, template, showMedia } =
        this.module.config.general;

      let alignItems = "center";
      let justifyContent = "center";
      let textAlign = "center";

      if (template.component === "TextCenterTop") {
        alignItems = "flex-start";
        justifyContent = "center";
        textAlign = "center";
      } else if (
        template.component === "TextCenterMiddle" ||
        template.component === "TextCenter" // legacy name
      ) {
        alignItems = "center";
        justifyContent = "center";
        textAlign = "center";
      } else if (template.component === "TextCenterBottom") {
        alignItems = "flex-end";
        justifyContent = "center";
        textAlign = "center";
      } else if (template.component === "TextLeftTop") {
        alignItems = "flex-start";
        justifyContent = "flex-start";
        textAlign = "left";
      } else if (
        template.component === "TextLeftMiddle" ||
        template.component === "TextLeft" // legacy name
      ) {
        alignItems = "center";
        justifyContent = "flex-start";
        textAlign = "left";
      } else if (template.component === "TextLeftBottom") {
        alignItems = "flex-end";
        justifyContent = "flex-start";
        textAlign = "left";
      }

      let borderRadius;
      if (this.isNestedModule) {
        borderRadius =
          this.parent.config.general.corners.value === "round"
            ? "var(--cr-border-radius-large)"
            : "initial";
      }

      return {
        backgroundColor: backgroundColor.value,
        textAlign,
        alignItems,
        justifyContent,
        borderRadius,
      };
    },
    textElStyles() {
      const { textColor } = this.module.config.text;

      return {
        color: textColor.value,
      };
    },
    headerElStyles() {
      const { textColor, isTextInCaps } = this.module.config.header;

      return {
        color: textColor.value,
        textTransform: isTextInCaps ? "uppercase" : "initial",
      };
    },
    ctaElStyles() {
      const { template } = this.module.config.general;

      let alignSelf = "center";
      if (template.component.startsWith("TextCenter")) {
        alignSelf = "center";
      } else if (template.component.startsWith("TextLeft")) {
        alignSelf = "flex-start";
      }

      return {
        alignSelf,
      };
    },
    nestedModulePadding() {
      if (this.isNestedModule) {
        return {
          paddingTop: "25px",
          paddingBottom: "25px",
        };
      } else {
        return {};
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../Gallery/galleryChildren";
@include reset;

.text-section {
  container-type: inline-size;
  contain: layout;

  display: flex;
  justify-content: center;

  &.-nested {
    min-height: 420px;
    overflow: hidden;
    isolation: isolate;
  }
}

.text-wrapper {
  margin: 0 auto;

  display: flex;
  flex-direction: column;
  gap: 25px;
  align-items: center;
  flex-grow: 1;

  max-width: 1600px;
  padding-inline: var(--cr-spacing-l);

  @include mobile {
    padding-inline: var(--cr-spacing-xs);
    width: 100%;
  }

  .-nested & {
    margin: 0;
    padding: var(--cr-spacing-xs);
  }

  .header {
    align-self: stretch;
    font-style: normal;
    hyphens: auto;

    @include font-family("heading");
    @include scale-fonts("heading-2");
    @include setup-richtext();

    .-nested & {
      @include scale-fonts("heading-1");
    }
  }

  .text {
    align-self: stretch;
    font-style: normal;

    @include font-family("body");
    @include scale-fonts("body");
    @include setup-richtext();
  }
}
</style>
